import { CLINICIAN_TYPE, THERAPY } from "@src/constants";
import type { SpecialityMatchType } from "@src/constants/filter-constants";
import type { FC, ReactNode } from "react";
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { useApplicationContext } from "./ApplicationContext";

const DEFAULT_TYPE_OF_SERVICE = THERAPY;
const DEFAULT_PROVIDER_TYPE = CLINICIAN_TYPE.THERAPIST;

const getProviderTypeForService = (serviceType?: string): string => {
  return serviceType === DEFAULT_TYPE_OF_SERVICE || !serviceType
    ? DEFAULT_PROVIDER_TYPE
    : CLINICIAN_TYPE.PRESCRIBER;
};

const shouldPersistCareFormat = (serviceType?: string, providerType?: string): boolean => {
  return (
    (serviceType || DEFAULT_TYPE_OF_SERVICE) === THERAPY &&
    (providerType || DEFAULT_PROVIDER_TYPE) === CLINICIAN_TYPE.THERAPIST
  );
};

const resolveInitialCareFormat = (
  persistedCareFormat: string | undefined,
  serviceType?: string,
  providerType?: string,
): string => {
  if (!shouldPersistCareFormat(serviceType, providerType)) {
    return "";
  }
  return persistedCareFormat || "Individual";
};

interface PrimaryFilters {
  location: string;
  typeOfService: string;
  ageRange: string;
  careFormat: string;
  insurancePolicy: string;
}

interface SecondaryFilters {
  providerType: string;
  specialities: string[];
  specialityMatchType: SpecialityMatchType;
  // modalities: string[];
  languages: string;
  providerGender: string;
  sessionType: string | string[];
  careFormat: string;
  providerRadius: string;
  dayOfWeek: string[];
  startHour?: number;
  endHour?: number;
}

export type { SecondaryFilters };

interface FilterContextProps {
  secondaryFilter: SecondaryFilters;
  setSecondaryFilter: Dispatch<SetStateAction<SecondaryFilters>>;
  resetSecondaryFilters: () => void;
  updateSecondaryFilter: (category: string, value: string) => void;
  secondaryFilterInitialState: SecondaryFilters;
  primaryFilter: PrimaryFilters;
  updatePrimaryFilter: (category: string, value: string) => void;
  setPrimaryFilter: Dispatch<SetStateAction<PrimaryFilters>>;
  primaryFilterInitialState: PrimaryFilters;
  resetPrimaryFilters: () => void;
}

// Create the context with default values
const FilterContext = createContext<FilterContextProps | undefined>(undefined);
const DEFAULT_SPECIALITY_MATCH_TYPE = "or";

const normalizeSpecialityMatchType = (value?: string): SpecialityMatchType => {
  return value === "and" ? "and" : DEFAULT_SPECIALITY_MATCH_TYPE;
};

// Provider component
export const FilterProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data } = useApplicationContext(); // Access Application context
  const resolvedProviderType = data?.providerType || getProviderTypeForService(data?.typeOfService);
  const resolvedCareFormat = resolveInitialCareFormat(
    data?.careFormat,
    data?.typeOfService,
    resolvedProviderType,
  );

  const secondaryFilterInitialState = {
    providerType: resolvedProviderType,
    specialities: data?.specialities || [],
    specialityMatchType: normalizeSpecialityMatchType(data?.specialityMatchType),
    // modalities: data?.modalities || [],
    languages: data?.languages || "",
    careFormat: resolvedCareFormat,
    providerGender: data?.providerGender || "",
    sessionType: data?.sessionType || "no preference",
    providerRadius: data?.providerRadius || "",
    dayOfWeek: data?.dayOfWeek || [],
    startHour: data?.startHour,
    endHour: data?.endHour,
  };

  const primaryFilterInitialState: PrimaryFilters = {
    location: data?.address?.state ?? "No Location Selected",
    typeOfService: data?.typeOfService || DEFAULT_TYPE_OF_SERVICE,
    ageRange: data?.ageRange ?? "No Age Range Selected",
    careFormat: resolvedCareFormat,
    insurancePolicy:
      data?.paymentMethod === "self_pay"
        ? "Self-pay" // Set to 'Self-pay' if paymentMethod is 'self_pay'
        : (data?.insurancePolicy?.name ?? "Unknown Insurance"),
  };
  const [secondaryFilter, setSecondaryFilter] = useState(secondaryFilterInitialState);
  const [primaryFilter, setPrimaryFilter] = useState<PrimaryFilters>(primaryFilterInitialState);

  // Function to reset all filters
  const resetSecondaryFilters = () => {
    setSecondaryFilter({
      ...secondaryFilterInitialState,
      specialities: [],
      specialityMatchType: DEFAULT_SPECIALITY_MATCH_TYPE,
      // Preserve In-person lock when under-12 flag is active
      // Stryker disable next-line OptionalChaining
      sessionType: data?.isUnder12InPersonProceed ? "In-person" : "no preference",
      // modalities: data?.modalities || [],
      languages: "",
      providerGender: "",
      providerRadius: "",
      dayOfWeek: [],
      startHour: undefined,
      endHour: undefined,
    });
  };

  const resetPrimaryFilters = () => {
    setPrimaryFilter(primaryFilterInitialState);
  };

  const updatePrimaryFilter = (category: string, value: string) => {
    setPrimaryFilter((prev) => {
      return {
        ...prev,
        [category]: value,
      };
    });
  };

  const updateSecondaryFilter = (category: string, value: string) => {
    setSecondaryFilter((prev) => {
      if (category === "specialityMatchType") {
        return {
          ...prev,
          specialityMatchType: DEFAULT_SPECIALITY_MATCH_TYPE,
        };
      }

      if (Array.isArray(prev[category])) {
        const existingCategory = prev[category] || [];
        const isValuePresent = existingCategory.some((item: string) => {
          return item === value;
        });

        const updatedCategory = isValuePresent
          ? existingCategory.filter((item: string) => {
              return item !== value;
            }) // Remove if already present
          : [...existingCategory, value]; // Add if not present

        return {
          ...prev,
          [category]: updatedCategory,
        };
      } else {
        if (category === "startHour" || category === "endHour") {
          return {
            ...prev,
            [category]: undefined,
          };
        }
        return {
          ...prev,
          [category]: "",
        };
      }
    });
  };

  const contextValue = useMemo(() => {
    return {
      secondaryFilter,
      setSecondaryFilter,
      resetSecondaryFilters,
      updateSecondaryFilter,
      secondaryFilterInitialState,
      primaryFilter,
      setPrimaryFilter,
      updatePrimaryFilter,
      primaryFilterInitialState,
      resetPrimaryFilters,
    };
  }, [secondaryFilter, primaryFilter]);

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
};

// Custom hook to use the filter context
export const useFilterContext = (): FilterContextProps => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
};
